import { IconButton, type PlaybackTransport } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { formatNumber } from "@/utils";
import { bandIndexAt, type ActionSourceBand, type ActionSourceModel } from "./actionSourceSegments";
import { actionSourceDotColor, actionSourceTerm } from "./actionSourceTerms";
import { bandDetailText } from "./segmentTimeline";
import { usePlaybackSelection } from "./usePlaybackSelection";
import styles from "./SegmentChipRow.module.css";

// 칩 줄이 화면을 너무 많이 차지하지 않게 하는 상한. 창원 실측 최대는 에피소드당 23개다.
// 상한을 넘는 띠는 구간 보기의 표가 모두 보여 준다.
const MAX_CHIPS = 48;

/**
 * 재생 바 바로 위의 구간 칩. 띠 하나에 칩 하나를 시간순으로 놓는다.
 *
 * 칩은 출처 색과 대문자 약칭(앞 세 글자)만 보인다. 이름과 시각은 아래 툴팁에 둔다.
 * 이름을 모두 표시하면 띠 19개가 네 줄로 접혀 영상 아래를 가린다. 약칭이면 칩 하나가 약
 * 48px 라서 한 줄에 들어간다.
 * - 칩을 누르면 영상을 그 구간 처음으로 옮긴다.
 * - 재생 위치를 포함하는 띠의 칩을 강조한다. 따로 선택하는 상태는 없다.
 * - c6 는 타임라인 띠를 `aria-hidden` 으로 둔다. 그래서 스크린 리더는 이 줄로만 띠에
 *   접근한다.
 */
export function SegmentChipRow({
  model,
  transport,
  onSeekToSeconds,
}: {
  model: ActionSourceModel;
  transport: PlaybackTransport | null;
  onSeekToSeconds?: (seconds: number) => void;
}): ReactNode {
  const bands = model.bands;
  // 띠 index 만 고른다. 그래서 재생 중 매 프레임이 아니라 띠가 바뀔 때만 다시 그린다.
  const current = usePlaybackSelection(transport, (snapshot) =>
    bandIndexAt(bands, snapshot.currentTimeSeconds),
  );
  const shown = bands.slice(0, MAX_CHIPS);
  const hidden = bands.length - shown.length;

  return (
    <div className={styles.row} role="group" aria-label="action_source 구간 칩">
      {shown.map((band, index) => (
        <SegmentChip
          key={band.id}
          band={band}
          current={index === current}
          onSeekToSeconds={onSeekToSeconds}
        />
      ))}
      {hidden > 0 ? (
        <span className={styles.more}>
          외 {formatNumber(hidden)}개가 더 있습니다. 구간 보기에서 모두 볼 수 있습니다.
        </span>
      ) : null}
    </div>
  );
}

function SegmentChip({
  band,
  current,
  onSeekToSeconds,
}: {
  band: ActionSourceBand;
  current: boolean;
  onSeekToSeconds?: (seconds: number) => void;
}): ReactNode {
  const term = actionSourceTerm(band.category, band.code);
  const caption = bandDetailText(band);
  return (
    <HatTextTooltip text={caption} placement="bottom" align="start">
      <IconButton
        type="button"
        size="sm"
        variant={current ? "solid" : "surface"}
        tone="neutral"
        aria-current={current ? "true" : undefined}
        // 색 점과 약칭은 장식으로 둔다. 칩에는 순번·시각을 표시할 수 없다.
        // 그래서 이 라벨이 이름을 전달한다.
        aria-label={`${caption}. 누르면 이 구간 처음으로 영상을 옮깁니다.`}
        disabled={onSeekToSeconds === undefined}
        onClick={() => onSeekToSeconds?.(Math.max(0, band.startSeconds))}
      >
        <span className={styles.mark} aria-hidden="true">
          <span className={styles.dot} style={{ background: actionSourceDotColor(term.dataViz) }} />
          {term.abbr}
        </span>
      </IconButton>
    </HatTextTooltip>
  );
}
